import { AlertCircle, ArrowRight, Clock, Lightbulb, Sparkles, Target, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ScrollArea, ScrollAreaScrollbar, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn, formatDateTime } from '@/lib/utils';
import type { SmartActionsRecommendation } from '@/types/ageing-analysis';

interface SmartActionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recommendation: SmartActionsRecommendation;
  onExecuteAction: (actionTitle: string) => void;
  formatCurrency?: (amount: number) => string;
  getPriorityBadgeColor: (priority: string) => string;
  getActionIcon: (actionType: string) => React.ReactNode;
  getActionButtonText: (actionType: string) => string;
}

export function SmartActionsDrawer({
  open,
  onOpenChange,
  recommendation,
  onExecuteAction,
  formatCurrency,
  getPriorityBadgeColor,
  getActionIcon,
  getActionButtonText,
}: SmartActionsDrawerProps) {
  // formatCurrency prop available for future currency formatting customization
  void formatCurrency;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[540px] p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <SheetTitle className="text-xl">AI Smart Actions</SheetTitle>
                <SheetDescription className="mt-1">{recommendation.customerName}</SheetDescription>
                <p className="text-xs text-muted-foreground mt-1">
                  Generated {formatDateTime(recommendation.generatedAt.toISOString())}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)]">
          <ScrollAreaViewport className="h-full">
            <div className="px-6 py-6 space-y-6">
              {/* Overall Strategy */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    <CardTitle className="text-base">Recommended Strategy</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm">{recommendation.overallStrategy}</p>
                </CardContent>
              </Card>

              {/* Key Insights */}
              {recommendation.keyInsights.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="h-5 w-5 text-yellow-600" />
                      <CardTitle className="text-base">Key Insights</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {recommendation.keyInsights.map((insight, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <span className="text-muted-foreground mt-0.5">•</span>
                          <span>{insight}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Risk Factors */}
              {recommendation.riskFactors.length > 0 && (
                <Card className="border-orange-200 bg-orange-50/50">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-orange-600" />
                      <CardTitle className="text-base text-orange-900">Risk Factors</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {recommendation.riskFactors.map((factor, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm text-orange-900">
                          <span className="text-orange-600 mt-0.5">•</span>
                          <span>{factor}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {/* Recommended Actions */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Recommended Actions</h3>
                  <Badge variant="secondary">{recommendation.actions.length} Actions</Badge>
                </div>

                {recommendation.actions.map((action, index) => (
                  <Card key={action.id} className="overflow-hidden">
                    <CardHeader className="pb-3 bg-muted/30">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={getPriorityBadgeColor(action.priority)}>
                            {action.priority.toUpperCase()}
                          </Badge>
                          <Badge variant="outline" className="capitalize">
                            {action.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">#{index + 1}</span>
                        </div>
                        <div className="p-1.5 rounded-md bg-background">{getActionIcon(action.actionType)}</div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-4">
                      {/* Title & Description */}
                      <div>
                        <h4 className="font-semibold mb-1">{action.title}</h4>
                        <p className="text-sm text-muted-foreground">{action.description}</p>
                      </div>

                      {/* Rationale */}
                      <div className="bg-muted/50 rounded-lg p-3">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Why this action?</p>
                        <p className="text-sm">{action.rationale}</p>
                      </div>

                      {/* Estimated Impact */}
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-3">Estimated Impact</p>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Recovery Probability</p>
                            <div className="flex items-center gap-2">
                              <Progress value={action.estimatedImpact.recoveryProbability} className="h-2 flex-1" />
                              <span className="text-xs font-medium">{action.estimatedImpact.recoveryProbability}%</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Risk Reduction</p>
                            <div className="flex items-center gap-2">
                              <Progress value={action.estimatedImpact.riskReduction} className="h-2 flex-1" />
                              <span className="text-xs font-medium">{action.estimatedImpact.riskReduction}%</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Time to Resolution</p>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span className="text-xs font-medium">{action.estimatedImpact.timeToResolution}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Suggested Timing */}
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">Suggested timing:</span>
                        <span className="font-medium">{action.suggestedTiming}</span>
                      </div>

                      {/* Prerequisites */}
                      {action.prerequisites && action.prerequisites.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Prerequisites:</p>
                          <ul className="space-y-1">
                            {action.prerequisites.map((prereq, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <span className="text-muted-foreground mt-0.5">›</span>
                                <span className="text-muted-foreground">{prereq}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Next Steps */}
                      <div>
                        <p className="text-sm font-medium mb-2">Next Steps:</p>
                        <ol className="space-y-2">
                          {action.nextSteps.map((step, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <span className="flex items-center justify-center h-5 w-5 rounded-full bg-primary/10 text-primary text-xs font-medium flex-shrink-0">
                                {i + 1}
                              </span>
                              <span>{step}</span>
                            </li>
                          ))}
                        </ol>
                      </div>

                      {/* Action Button */}
                      <Button
                        className={cn(
                          'w-full',
                          action.priority === 'critical' || action.priority === 'high' ? '' : 'variant-outline',
                        )}
                        variant={action.priority === 'critical' || action.priority === 'high' ? 'default' : 'outline'}
                        onClick={() => onExecuteAction(action.title)}
                      >
                        {getActionButtonText(action.actionType)}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Footer Note */}
              <Card className="border-dashed">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium mb-1">AI-Powered Recommendations</p>
                      <p className="text-xs text-muted-foreground">
                        These recommendations are generated by AI based on customer data, payment history, and
                        collection best practices. Please review and apply professional judgment before taking action.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollAreaViewport>
          <ScrollAreaScrollbar orientation="vertical" />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
